import React, { useEffect, useMemo, useRef, useState } from "react";
import AccountsTable from "@components/AccountsTable";
import axios from "axios";

/** -------- helpers -------- */
function detectAccountState(a) {
  // под разные схемы: banned/isBanned/status/banReason
  if (a?.isBanned === true) return "banned";
  if (a?.banned === true) return "banned";
  if (String(a?.status || "").toLowerCase() === "banned") return "banned";
  if (String(a?.state || "").toLowerCase() === "banned") return "banned";
  if (a?.banReason) return "banned";
  return "active";
}

function FilterDropdown({ open, anchorRef, value, onChange, onClose, tasks = [] }) {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const onDown = (e) => {
      const panel = panelRef.current;
      const anchor = anchorRef?.current;
      if (!panel) return;

      if (panel.contains(e.target)) return;
      if (anchor && anchor.contains(e.target)) return;
      onClose?.();
    };

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  const box =
    "rounded-[16px] border border-[#E5E7EB] bg-white shadow-[0_18px_40px_rgba(0,0,0,0.12)]";

  const label = "text-[12px] font-semibold text-[#374151]";
  const hint = "text-[11px] text-[#6B7280]";

  const pill =
    "px-3 py-2 rounded-[14px] border border-[#E5E7EB] hover:bg-[#F9FAFB] transition cursor-pointer flex items-center gap-2";

  return (
    <div
      ref={panelRef}
      className={`absolute right-0 top-[48px] w-[320px] ${box} p-4`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[14px] font-semibold text-[#111827]">Фильтр</div>
          <div className={`mt-0.5 ${hint}`}>Показывать только нужные аккаунты.</div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="w-9 h-9 rounded-full bg-[#F3F4F6] grid place-items-center hover:bg-[#EDEFF3] transition"
          title="Закрыть"
        >
          <i className="fa-solid fa-xmark text-[#111827]" />
        </button>
      </div>

      <div className="mt-4 grid gap-3">
        <div className={label}>Статус</div>

        <label className={pill}>
          <input
            type="radio"
            name="accFilter"
            checked={value.state === "all"}
            onChange={() => onChange({ ...value, state: "all" })}
            className="accent-[#2B7CFF]"
          />
          <span className="text-[13px] text-[#111827]">Все</span>
        </label>

        <label className={pill}>
          <input
            type="radio"
            name="accFilter"
            checked={value.state === "active"}
            onChange={() => onChange({ ...value, state: "active" })}
            className="accent-[#2B7CFF]"
          />
          <span className="text-[13px] text-[#111827]">Активные</span>
        </label>

        <label className={pill}>
          <input
            type="radio"
            name="accFilter"
            checked={value.state === "banned"}
            onChange={() => onChange({ ...value, state: "banned" })}
            className="accent-[#2B7CFF]"
          />
          <span className="text-[13px] text-[#111827]">Забаненные</span>
        </label>

        <div className="mt-4 grid gap-3">
          <div className={label}>Привязка</div>

          <label className={pill}>
            <input
              type="radio"
              name="bindFilter"
              checked={value.bind === "all"}
              onChange={() => onChange({ ...value, bind: "all", taskUid: "" })}
              className="accent-[#2B7CFF]"
            />
            <span className="text-[13px] text-[#111827]">Все</span>
          </label>

          <label className={pill}>
            <input
              type="radio"
              name="bindFilter"
              checked={value.bind === "free"}
              onChange={() => onChange({ ...value, bind: "free", taskUid: "" })}
              className="accent-[#2B7CFF]"
            />
            <span className="text-[13px] text-[#111827]">Только свободные</span>
          </label>

          <label className={pill}>
            <input
              type="radio"
              name="bindFilter"
              checked={value.bind === "reserved"}
              onChange={() => onChange({ ...value, bind: "reserved", taskUid: "" })}
              className="accent-[#2B7CFF]"
            />
            <span className="text-[13px] text-[#111827]">Только привязанные</span>
          </label>

          <label className={pill}>
            <input
              type="radio"
              name="bindFilter"
              checked={value.bind === "task"}
              onChange={() => onChange({ ...value, bind: "task" })}
              className="accent-[#2B7CFF]"
            />
            <span className="text-[13px] text-[#111827]">К задаче</span>

            <select
              value={value.taskUid || ""}
              onChange={(e) => onChange({ ...value, taskUid: e.target.value, bind: "task" })}
              className="ml-auto h-8 px-2 rounded-md border border-[#E5E7EB] bg-white text-[12px] text-[#111827]"
            >
              <option value="">Выбери…</option>
              {Array.isArray(tasks) &&
                tasks.map((t) => (
                  <option key={t.uid} value={String(t.uid)}>
                    #{t.uid} — {t.taskName || "Без названия"}
                  </option>
                ))}
            </select>
          </label>
        </div>

        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={() => onChange({ state: "all", bind: "all", taskUid: "" })}
            className={`h-10 px-4 rounded-full border border-[#D6D9E0] bg-white hover:bg-[#F7F8FA] transition text-sm font-medium ${
              value.state === "all" ? "opacity-50 pointer-events-none" : ""
            }`}
          >
            Сбросить
          </button>

          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-full bg-[#2B7CFF] text-white shadow-[0_12px_28px_rgba(43,124,255,0.35)] hover:brightness-95 transition text-sm font-semibold"
          >
            Применить
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [proxies, setProxies] = useState([]);
  const [tasks, setTasks] = useState([]);

  const [query, setQuery] = useState("");

  // dropdown filter state
  const [filterOpen, setFilterOpen] = useState(false);
  const filterBtnRef = useRef(null);
    const [filters, setFilters] = useState({ state: "all", bind: "all", taskUid: "" }); // state: all|active|banned; bind: all|free|reserved|task

  async function loadAll() {
        const [aRes, pRes, tRes] = await Promise.all([
      fetch("/api/accounts"),
      fetch("/api/proxies"),
      fetch("/api/tasks"),
    ]);

    const a = await aRes.json().catch(() => []);
    const p = await pRes.json().catch(() => []);
    const t = await tRes.json().catch(() => []);

    setAccounts(Array.isArray(a) ? a : []);
        setProxies(Array.isArray(p) ? p : []);
    setTasks(Array.isArray(t) ? t : []);
  }

  useEffect(() => {
    loadAll().catch(() => {});
  }, []);

  const proxyMap = useMemo(() => {
    const m = {};
    for (const p of proxies) m[p.uid] = p.proxyUrl;
    return m;
  }, [proxies]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return accounts.filter((a) => {
      // 1) status filter
      const state = detectAccountState(a);
      if (filters.state === "active" && state !== "active") return false;
      if (filters.state === "banned" && state !== "banned") return false;

      // 1.5) bind filter (reserved/free)
      if (filters.bind === "free" && a?.reservedTaskUid != null) return false;
      if (filters.bind === "reserved" && a?.reservedTaskUid == null) return false;
      if (filters.bind === "task") {
        const tu = Number(filters.taskUid);
        if (!tu) return false;
        if (Number(a?.reservedTaskUid) !== tu) return false;
      }

      // 2) search by uid/token/proxy
      if (!q) return true;

      const uidStr = String(a.uid ?? "").toLowerCase();
      const token = String(a.tokenVk || "").toLowerCase();
      const proxy = String(proxyMap?.[a.proxyId] || "").toLowerCase();

      return uidStr.includes(q) || token.includes(q) || proxy.includes(q);
    });
  }, [accounts, query, proxyMap, filters]);

    const filterBadgeCount = useMemo(() => {
    let c = 0;
    if (filters.state !== "all") c += 1;
    if (filters.bind !== "all") c += 1;
    if (filters.bind === "task" && filters.taskUid) c += 1;
    return c;
  }, [filters]);

  async function deleteOne(uid) {
    if (!window.confirm(`Удалить аккаунт uid=${uid}?`)) return;

    try {
      const { data } = await axios.delete(`/api/accounts/${uid}`);

      if (!data?.success) {
        alert(data?.message || "Ошибка при удалении аккаунта");
        return;
      }

      alert(`Аккаунт uid=${uid} удалён`);
      await loadAll().catch(() => {});
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Ошибка соединения с сервером");
    }
  }

  return (
    <div className="min-h-screen">
      <main className="px-6 pb-14">
        <div className="mx-auto max-w-[1220px]">
          <div className="mt-10 flex items-center justify-between gap-6">
            <h1 className="text-[28px] font-semibold text-[#111827]">Список аккаунтов</h1>

            <div className="flex items-center gap-3 relative">
              {/* Search */}
              <div className="h-10 w-[360px] border border-[#C8C8C8] bg-white rounded-full shadow-[0_10px_25px_rgba(0,0,0,0.06)] px-4 flex items-center gap-2">
                <i className="fa-solid fa-magnifying-glass text-[#9CA3AF] w-6" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full outline-none text-sm placeholder:text-[#9CA3AF]"
                  placeholder="Поиск по uid / token / proxy"
                />
              </div>

              {/* Filter */}
              <button
                ref={filterBtnRef}
                onClick={() => setFilterOpen((v) => !v)}
                className="relative h-10 px-4 border border-[#C8C8C8] bg-white rounded-full shadow-[0_10px_25px_rgba(0,0,0,0.06)] flex items-center gap-2 text-sm font-medium text-[#111827] hover:opacity-90 transition"
                type="button"
              >
                <i className="fa-solid fa-arrow-up-short-wide h-3" />
                Фильтр
                {filterBadgeCount > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-[#EEF2FF] text-[#3730A3] border border-[#C7D2FE]">
                    {filterBadgeCount}
                  </span>
                )}
              </button>

              <FilterDropdown
                open={filterOpen}
                tasks={tasks}
                anchorRef={filterBtnRef}
                value={filters}
                onChange={setFilters}
                onClose={() => setFilterOpen(false)}
              />

              {/* Add */}
            </div>
          </div>

          <div className="mt-3 text-[12px] text-[#6B7280]">
            Найдено:{" "}
            <span className="font-semibold text-[#111827]">{filtered.length}</span>
            {query.trim() && (
              <>
                {" "}
                • запрос:{" "}
                <span className="font-semibold text-[#111827]">"{query.trim()}"</span>
              </>
            )}
            {filters.state !== "all" && (
              <>
                {" "}
                • фильтр:{" "}
                <span className="font-semibold text-[#111827]">{filters.state}</span>
              </>
            )}
          </div>

          <div className="mt-7">
            <AccountsTable rows={filtered} proxyMap={proxyMap} onDeleteOne={deleteOne} />
          </div>
        </div>
      </main>
    </div>
  );
}
